import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { TwcSliderValue } from '@topcon/twc-angular';

import { NumberHelperService } from '@mirage-projects/cw-utils';

export interface SlideBarComponentData {
    min: number;
    max: number;
    step: number;
    label?: string;
    isLogarithmic?: boolean;
    showRanges?: boolean;
    convertValueSliderFunction?: (value: number) => number; // Function to convert the internal value of the slider to the value shown in the input.
    convertValueInputFunction?: (value: number) => number; // Function to convert the value displayed in the input to the internal value of the slider.
}
/**
 * It shows a slide bar to select a value between an max and a min
 */
@Component({
    selector: 'cw-slide-bar',
    templateUrl: './slide-bar.component.html',
    styleUrls: ['./slide-bar.component.scss'],
    standalone: false,
})
export class SlideBarComponent implements OnChanges {
    /**
     * value from input and Slide
     */
    @Input() value!: number;

    /**
     * data to set the slide-bar configuration
     */
    @Input() data: SlideBarComponentData = {
        min: 0,
        max: 0,
        step: 0,
        label: '',
        isLogarithmic: false,
    };

    /** Indicates if the component is disabled */
    @Input() disabled = false;

    /** Indicates if the component is readonly */
    @Input() readonly = false;

    /** Indicates if the number is integer or float */
    @Input() isInteger = false;

    /** Indicates if the input is displayed */
    @Input() showInput = true;

    /** Indicates the number of decimals that will be displayed. */
    @Input() numberOfDecimals = 2;

    /**
     * event that emits when the slider or the input fields have their values modified
     */
    @Output() changeValue: EventEmitter<number> = new EventEmitter<number>();

    /* These properties are necessary because de field can be  logarithmic*/
    valueSlider!: number;
    rangeMin!: number;
    rangeMax!: number;
    stepSlider = 0.00001;

    inputValue!: number;

    // TODO: This is a workaround related to TWC emitting initial value. Remove when fixed. Related task: TWC-1340.
    isFirstRun = true;

    // Workaround: To avoid emitting the onChange event when the component's value is changed from the external
    isExternalChange = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private numberHelperService: NumberHelperService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.numberOfDecimals >= 0 && changes['numberOfDecimals']) {
            this.applyDecimals();
        }
        if (this.data && changes['data']) {
            this.initializeData();
        }
        if (changes['value']?.currentValue !== changes['value']?.previousValue) {
            this.init();
            this.isExternalChange = true;
            setTimeout(() => {
                this.isExternalChange = false;
            }, 20);
        }
    }

    init(): void {
        this.initFunctions();
        this.inputValue = this.value;
        this.initializeData();
    }

    changeValueInput(): void {
        this.inputValue = Math.min(this.inputValue, this.data.max);
        this.inputValue = Math.max(this.inputValue, this.data.min);
        this.inputValue = this.toDecimals(this.inputValue, this.numberOfDecimals);

        this.valueSlider =
            this.data.isLogarithmic && this.data.convertValueInputFunction
                ? this.data.convertValueInputFunction(this.inputValue)
                : this.inputValue;
        this.changeValue.emit(this.inputValue);
    }

    changeValueSlider(value: TwcSliderValue): void {
        /// TODO: This is a workaround related to TWC emitting initial value. Remove when fixed. Related task: TWC-1340.
        if (this.isFirstRun) {
            this.isFirstRun = false;
            return;
        }

        const sliderValue = value as number;
        this.inputValue =
            this.data.isLogarithmic && this.data.convertValueSliderFunction
                ? this.data.convertValueSliderFunction(sliderValue)
                : sliderValue;

        this.inputValue = this.toDecimals(this.inputValue, this.numberOfDecimals);

        this.cdr.detectChanges();
        if (!this.isExternalChange) {
            this.changeValue.emit(this.inputValue);
        }
    }

    private initializeData(): void {
        this.stepSlider = this.data.isLogarithmic ? 0.00001 : this.data.step;

        if (this.data.isLogarithmic && this.data.convertValueInputFunction) {
            this.valueSlider = this.data.convertValueInputFunction(this.value);
            // For a logaritmic conversion, we need the values from the slider between [0-100]. Using the logaritmic function, we will convert a value in this range to [data.min-data.max] range.
            this.rangeMin = 0;
            this.rangeMax = 100;
        } else {
            this.valueSlider = this.value;
            this.rangeMin = this.data.min;
            this.rangeMax = this.data.max;
        }
        this.applyDecimals();
    }

    private applyDecimals(): void {
        this.inputValue = this.toDecimals(this.inputValue, this.numberOfDecimals);
        this.rangeMin = this.toDecimals(this.rangeMin, this.numberOfDecimals);
        this.rangeMax = this.toDecimals(this.rangeMax, this.numberOfDecimals);
    }

    private initFunctions(): void {
        if (this.data.isLogarithmic) {
            if (!this.data.convertValueSliderFunction) {
                this.data.convertValueSliderFunction = (value): number => Math.pow(1.1, value);
            }
            if (!this.data.convertValueInputFunction) {
                this.data.convertValueInputFunction = (value): number => Math.log(value) / Math.log(1.1);
            }
        }
    }

    private toDecimals(n: string | number, decimals: number): number {
        const res = this.numberHelperService.toFixedWithPrecision(Number(n), decimals);
        return Number(res);
    }
}
